import { useState } from 'react'
import { Download, FileSpreadsheet, FileText, Printer } from 'lucide-react'

import { Button } from 'components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { toast } from 'components/ui/toast-store'

import * as explorerService from '../services/explorerService'
import type { ExplorerExportFormat } from '../services/explorerService'
import type { ExplorerGrainKey, ExplorerReportDefinition } from '../types/explorer.types'

interface ExportMenuProps {
	grain: ExplorerGrainKey
	definition: ExplorerReportDefinition
	/** Names the file. The loaded report's name, or the grain's label. */
	reportName: string
	disabled?: boolean
}

const FORMATS: {
	format: ExplorerExportFormat
	label: string
	hint: string
	icon: typeof FileText
}[] = [
	{
		format: 'xlsx',
		label: 'Excel',
		// The reason to prefer it, stated where the choice is made.
		hint: 'Numbers and dates stay typed',
		icon: FileSpreadsheet,
	},
	{
		format: 'csv',
		label: 'CSV',
		hint: 'Opens anywhere',
		icon: FileText,
	},
	{
		format: 'pdf',
		label: 'PDF',
		// Says what it is FOR, since the trade-off (fewer columns, capped
		// rows) only makes sense once you know it is the send-it format.
		hint: 'For sharing and printing',
		icon: Printer,
	},
]

/**
 * Export the current report (MYD-364, formats added in MYD-376).
 *
 * The export runs the CURRENT definition, including unsaved edits, because the
 * user is asking for what is on screen. Exporting the last-saved version
 * instead would quietly hand them a different report from the one they are
 * looking at.
 */
export function ExportMenu({ grain, definition, reportName, disabled }: ExportMenuProps) {
	const [busy, setBusy] = useState<ExplorerExportFormat | null>(null)

	const download = async (format: ExplorerExportFormat) => {
		setBusy(format)
		try {
			const { blob, filename } = await explorerService.exportReport({
				grain,
				name: reportName,
				format,
				...definition,
			})

			// Object URL from an XHR blob, never a navigation to the endpoint.
			// CRA's dev proxy ignores requests carrying a `text/html` Accept
			// header, so a window.open or a plain link 404s in development while
			// working in production — the MYD-344 trap.
			const url = URL.createObjectURL(blob)
			try {
				const anchor = document.createElement('a')
				anchor.href = url
				anchor.download = filename
				document.body.appendChild(anchor)
				anchor.click()
				anchor.remove()
			} finally {
				// Revoking immediately can cancel the download in some browsers,
				// which is why this waits rather than firing in the same tick.
				setTimeout(() => URL.revokeObjectURL(url), 1000)
			}
		} catch (error: any) {
			// The server's message names the offending column or explains that the
			// report is too expensive to run. Losing it would leave the user with
			// nothing to act on.
			toast.error(error?.explorerMessage || 'Could not export this report')
		} finally {
			setBusy(null)
		}
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" isOutline disabled={disabled || busy !== null}>
					<Download className="w-4 h-4 mr-2" />
					{busy ? 'Preparing...' : 'Export'}
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="end" className="w-56">
				{FORMATS.map(({ format, label, hint, icon: Icon }) => (
					<DropdownMenuItem key={format} onClick={() => download(format)}>
						<Icon className="w-4 h-4 mr-2 shrink-0 text-gray-400" />
						<span className="flex flex-col">
							<span>{label}</span>
							<span className="text-xs text-gray-500">{hint}</span>
						</span>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default ExportMenu
